import { graphql } from "gatsby";
import React from "react";
import styled from "styled-components";

import {
  IFixed,
  IFluid,
  ILocalFile
} from "src/types/local-files";

import { IImageMeta } from "../types/local-files";
import HeroTags from "../components/hero-tags";
import ArticlesGrid from "../components/articles-grid";
import { IArticleData } from "../components/articles-grid/articles-grid";
import CollapseTags from "../components/collapse-tags/collapse-tags";
import { FullWidthColumnWrapper } from "../components/confines-wrappers";
import { IFieldTechnicalArticle } from "../components/field-technical-article/field-technical-article";
import Footer from "../components/footer";
import FullWidthTeaser from "../components/full-width-teaser";
import GetInTouch from "../components/get-in-touch";
import Header from "../components/header";
import HelmetWrapper from "../components/helmet-wrapper";
import { breadCrumbSchema } from "../components/html-schema-snippets";
import Layout from "../components/layout";
import PageTop from "../components/page-top";
import Pagination from "../components/pagination";
import StayInTheLoop from "../components/stay-in-the-loop/stay-in-the-loop";
import getProfile from "../templates/utils/getProfile";
import {
  colourHelper,
  fontLineHeightHelper,
  fontSizeHelper,
  fontWeightHelper
} from "../theme/theme-helper";
import { IProfileData } from "./about-us";

export interface IBlogArticleData {
  title: string;
  id: string;
  created: string;
  changed: string;
  field_featured: boolean;
  field_approx_reading_time: string;
  path: {
    alias: string;
  };
  field_meta_description: string;
  field_cover_image: IImageMeta;
  field_cover_excerpt: string;
  relationships: {
    field_technical_article: IFieldTechnicalArticle[];
    field_latest_tags?: IFieldLatestTag[];
    author: IProfileData;
    field_meta_image?: ILocalFile<IFixed>;
    field_cover_image: ILocalFile<IFluid>;
    field_hero_image: ILocalFile<IFixed>;
    field_mobile_hero_image: ILocalFile<IFixed>;
    field_latest_related_content: Array<{
      title: string;
      created: string;
      path: {
        alias: string;
      };
      relationships: {
        author: IProfileData;
        field_cover_image: ILocalFile<IFluid>;
      };
    }>;
  };
}

export interface IFieldLatestTag {
  name: string;
  path: {
    alias: string;
  };
}

export interface INodeData {
  node: IBlogArticleData;
}

export interface ITaxonomyTermLatest {
  node: {
    name: string;
    path: {
      alias: string;
    };
  };
}

interface ILatestProps {
  totalLatest: {
    totalCount: number;
  };
  nodeLatestOverview: {
    created: string;
    changed: string;
    title: string;
    field_hero_heading: string;
    field_meta_description: string;
    relationships: {
      field_featured_latest_article: IBlogArticleData;
    };
  };
  allNodeBlogArticle: {
    edges: INodeData[];
  };
  allTaxonomyTermLatest: {
    edges: ITaxonomyTermLatest[];
  };
}

export const PageTopCustom = styled.div`
  .top-content {
    padding-top: 10%;
    padding-bottom: 10%;

    .heading {
      transition: 0.2s ease-in-out;
      display: block;
      margin-bottom: 0;
      color: ${({ theme }) => colourHelper({
    theme,
    variant: "black"
  })};
      font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "subhead_2"
    })};
      font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "heavy"
    })};
      line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
    }
  }

  @media (min-width: 768px) {
    .top-content {
      padding-top: 8.8rem;
      padding-bottom: 8.8rem;

      .heading {
        max-width: 66rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_2"
    })};
        line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "small"
    })};
      }

      .hero-tags-container {
        li {
          width: 20%;
        }
      }
    }
  }

  @media (min-width: 960px) {
    .top-content {
      .heading {
        max-width: 76rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_3"
    })};
      }
    }
  }

  @media (min-width: 1024px) {
    .top-content {
      .heading {
        max-width: 84rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_4"
    })};
      }
    }
  }

  @media (min-width: 1280px) {
    .top-content {
      .heading {
        max-width: 94rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_5"
    })};
      }
    }
  }
`;

export const ArticlesGridWrapper = styled.div`
  .articles-grid-container {
    padding-top: 16%;
    padding-bottom: 16%;
  }

  @media (min-width: 568px) {
    .articles-grid-container {
      padding-top: 8.8rem;
      padding-bottom: 8.8rem;
    }
  }

  @media (min-width: 600px) {
    .articles-grid-container {
      padding-top: 9.6rem;
      padding-bottom: 9.6rem;
    }
  }
`;

const Latest = ({ data }: { data: ILatestProps }) => {
  const breadcrumbsSchemaData = breadCrumbSchema([
    {
      name: "Latest",
      path: "/latest"
    }
  ]);

  const schemaJson = [ breadcrumbsSchemaData ];

  const featuredArticle =
    data.nodeLatestOverview.relationships.field_featured_latest_article;

  const filteredArticles = data.allNodeBlogArticle.edges.filter(edge => {
    return edge.node.id !== featuredArticle.id;
  });

  // Add the featured article to the start of the filteredArticles list
  filteredArticles.unshift({ node: featuredArticle });

  return (
    <Layout className="latest">
      <>
        <HelmetWrapper
          title={data.nodeLatestOverview.title}
          description={data.nodeLatestOverview.field_meta_description}
          updatedAt={data.nodeLatestOverview.changed}
          canonicalPath="/latest"
        >
          <script type="application/ld+json">
            {JSON.stringify(schemaJson)}
          </script>
        </HelmetWrapper>

        <header
          role="banner"
          id="header"
        >
          <Header
            initialLogo
            initialTheme="light"
          />

          <PageTopCustom id="hero">
            <PageTop
              gradient="lightPinkPurple"
              height="small"
            >
              <FullWidthColumnWrapper className="top-content">
                <p className="heading">
                  {data.nodeLatestOverview.field_hero_heading}
                </p>

                <HeroTags
                  initialTheme="light"
                  featuredPath="/latest"
                  tags={data.allTaxonomyTermLatest.edges.map(tag => {
                    return {
                      name: tag.node.name,
                      path: tag.node.path.alias
                    };
                  })}
                />
              </FullWidthColumnWrapper>
            </PageTop>
          </PageTopCustom>

          <CollapseTags
            root="/latest"
            rootTerm="Everything"
            tags={data.allTaxonomyTermLatest.edges}
          />
        </header>

        <main
          id="main-content"
          role="main"
        >
          <h1 className="off-screen">
            {data.nodeLatestOverview.title}
          </h1>

          {filteredArticles.slice(0, 6).map((post: INodeData, i) => {
            const featured: boolean = i === 0;
            const profile = getProfile(post.node.relationships.author);

            return (
              <FullWidthTeaser
                key={post.node.id}
                created={post.node.created}
                path={post.node.path.alias}
                title={post.node.title}
                author={profile.field_full_name}
                cover={post.node.relationships.field_cover_image}
                alt={post.node.field_cover_image.alt}
                featured={featured}
                excerpt={post.node.field_cover_excerpt}
              />
            );
          })}

          <ArticlesGridWrapper>
            <ArticlesGrid
              articles={filteredArticles.slice(6, 12).map((post: INodeData) => {
                const profile = getProfile(post.node.relationships.author);

                const articleObj: IArticleData = {
                  created: post.node.created,
                  path: post.node.path.alias,
                  title: post.node.title,
                  author: profile.field_full_name,
                  cover: post.node.relationships.field_cover_image
                };

                return articleObj;
              })}
            />
          </ArticlesGridWrapper>

          <aside>
            <StayInTheLoop />
          </aside>

          <ArticlesGridWrapper>
            <ArticlesGrid
              articles={filteredArticles
                .slice(12, 18)
                .map((post: INodeData) => {
                  const profile = getProfile(post.node.relationships.author);

                  const articleObj: IArticleData = {
                    created: post.node.created,
                    path: post.node.path.alias,
                    title: post.node.title,
                    author: profile.field_full_name,
                    cover: post.node.relationships.field_cover_image
                  };

                  return articleObj;
                })}
            />
          </ArticlesGridWrapper>

          <aside>
            <FullWidthColumnWrapper>
              <Pagination
                current={0}
                total={data.totalLatest.totalCount}
                limit={18}
                slug="/latest"
              />
            </FullWidthColumnWrapper>
          </aside>
        </main>

        <footer role="contentinfo">
          <GetInTouch />

          <Footer />
        </footer>
      </>
    </Layout>
  );
};

export const query = graphql`
  query latestQuery($latestLimit: Int = 18, $tagLimit: Int = 4) {
    totalLatest: allNodeBlogArticle {
      totalCount
    }
    nodeLatestOverview {
      created
      changed
      title
      field_hero_heading
      field_meta_description
      relationships {
        field_featured_latest_article {
          ...getLatestData
        }
      }
    }
    ...getAllBlogs
    ...getLatestTags
  }
`;

export default Latest;
